/*
 * @fileOverView Accordion component definition
 */

import Vue from 'vue';
import EventBus from 'utils/EventBus';
import QueryString from 'utils/QueryString';

/**
 * Accordion Vue
 *
 * @type {class}
 */
Vue.component('accordion-vue', {
  props: {
    // Needed to help determine when all items are expanded or collapsed.
    'itemCount': Number
  },

  data: function() {
    return {
      expandedItems: []
    }
  },

  computed: {
    /**
     * Determines whether all items are expanded or not.
     */
    allExpanded: function () {
      return this.expandedItems.length === this.itemCount;
    }
  },

  mounted() {
    this.deepLink();

    // The new history may deep link to a different accordion item.
    EventBus.$on('history-state-pushed', this.deepLink);
  },

  methods: {
    /**
     * Expands every item in the accordion.
     */
    expandAll(e) {
      e.preventDefault();

      for (let i = 0; i < this.itemCount; i++) {
        this.expandItem(i, true);
      }
    },

    /**
     * Collapses every item in the accordion.
     */
    collapseAll(e) {
      e.preventDefault();

      for (let i = 0; i < this.itemCount; i++) {
        this.collapseItem(i, true);
      }
    },

    /**
     * Toggles an individual item in the accordion.
     *
     * @param  {Number} key - The key of the item to be toggled.
     */
    toggleItem(key) {
      if (!this.isItemExpanded(key)) {
        this.expandItem(key);
      }
      else {
        this.collapseItem(key);
      }
    },

    /**
     * Expands an individual item in the accordion.
     *
     * @param  {Number} key - The key of the item to be expanded.
     * @param  {Boolean} bulk - Whether this is part of a bulk "All" operation.
     */
    expandItem(key, bulk = false) {
      if (!this.isItemExpanded(key)) {
        this.expandedItems.push(key);

        // Focus the proper element for keyboard navigation.
        this.$nextTick(() => {
          if (!bulk) {
            this.$refs[`collapseToggle${key}`].focus();
          }
          else if (this.allExpanded) {
            this.$refs.collapseAll.focus();
          }
        });

        // Animate open to `height: auto`.
        const $itemContent = $(this.$refs[`content${key}`]);
        const autoHeight = $itemContent.css('height', 'auto').innerHeight();
        $itemContent.height(0).animate({height: autoHeight}, 200, 'swing', () => {
          $itemContent.css('height', 'auto');
        });
      }
    },

    /**
     * Collapses an individual item in the accordion.
     *
     * @param  {Number} key - The key of the item to be expanded.
     * @param  {Boolean} bulk - Whether this is part of a bulk "All" operation.
     */
    collapseItem(key, bulk = false) {
      if (this.isItemExpanded(key)) {
        const $itemContent = $(this.$refs[`content${key}`]);

        $itemContent.animate({height: 0}, {
          duration: 200,
          complete: () => {
            this.expandedItems = this.expandedItems.filter(i => i !== key);

            // Focus the proper element for keyboard navigation.
            this.$nextTick(() => {
              if (!bulk) {
                this.$refs[`expandToggle${key}`].focus();
              }
              else if (!this.allExpanded) {
                this.$refs.expandAll.focus();
              }
            });
          }
        });
      }
    },

    /**
     * Checks whether or not an individual item is expanded.
     *
     * @param  {Number} key - The key of the item to check.
     * @return {Boolean} Whether or not the item is expanded.
     */
    isItemExpanded(key) {
      return this.expandedItems.indexOf(key) !== -1;
    },

    /**
     * Expands a directly-linked item and scrolls to it.
     *
     * The item must contain a `data-deep-link` attribute value that matches the
     * value of a `item` URL query parameter, such as `?item=donation-contact`.
     */
    deepLink() {
      const linkedItem = this.$el.querySelector(`[data-deep-link="${QueryString('item')}"]`);

      if (linkedItem) {
        this.expandItem($(linkedItem).index());

        this.$nextTick(() => {
          $('html, body').animate({
           'scrollTop': $(linkedItem).offset().top + 1
          }, 500);
        });
      }
    }
  }
});
